import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Megaphone, Send, Pause, Play, Trash2, Plus, Loader2, Users, FileText, Smartphone, Image as ImageIcon, Film, Check, X, ChevronLeft, ChevronRight, Upload, Eye, Paperclip, Hash, Pencil, Save, Calendar, MessageCircle, ExternalLink, MousePointerClick, Activity, Phone } from 'lucide-react';
import axios from './wabotAxios';
import { toast } from 'sonner';

import { WABOT_API, WABOT_BACKEND_ORIGIN } from './wabotApi';
const API = WABOT_API;
const BACKEND_ORIGIN = WABOT_BACKEND_ORIGIN;
const DEFAULT_SAMPLE_NUMBER = '+972504944192';

const STEPS = [
  { id: 1, label: 'בחירת מכשיר' },
  { id: 2, label: 'הקמת תבנית שיווקית' },
  { id: 3, label: 'קבוצת איש קשר' },
  { id: 4, label: 'יבוא אנשי קשר' },
  { id: 5, label: 'שליחת דוגמא' },
  { id: 6, label: 'שליחה קבוצתית' },
];

const WaBotMarketing = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [campaignName, setCampaignName] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [template, setTemplate] = useState('');
  const [media, setMedia] = useState(null); // { url, type }
  const [delayMin, setDelayMin] = useState(30);
  const [delayMax, setDelayMax] = useState(80);
  const [batchSizeMin, setBatchSizeMin] = useState(18);
  const [batchSizeMax, setBatchSizeMax] = useState(22);
  const [pauseMinMinutes, setPauseMinMinutes] = useState(55);
  const [pauseMaxMinutes, setPauseMaxMinutes] = useState(75);
  const [aiVary, setAiVary] = useState(false);
  const [aiVariationCount, setAiVariationCount] = useState(20);
  const [groupId, setGroupId] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [sampleNumber, setSampleNumber] = useState(DEFAULT_SAMPLE_NUMBER);
  const [sampleSent, setSampleSent] = useState(false);
  const [launching, setLaunching] = useState(false);

  const [resumeModal, setResumeModal] = useState(null);
  const [resumeSession, setResumeSession] = useState('');
  // Campaign details panel state
  const [detailCampaign, setDetailCampaign] = useState(null);

  // Optional scheduled launch (date + time, Asia/Jerusalem). Empty = launch now.
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(''); // YYYY-MM-DD
  const [scheduleTime, setScheduleTime] = useState(''); // HH:MM

  const fetchAll = useCallback(async () => {
    try {
      const [c, s, g, t] = await Promise.all([
        axios.get(`${API}/wabot/campaigns`),
        axios.get(`${API}/wabot/sessions`),
        axios.get(`${API}/wabot/contact-groups`),
        axios.get(`${API}/wabot/templates`),
      ]);
      setCampaigns(c.data.campaigns || []);
      setSessions(s.data.sessions || []);
      setGroups(g.data.groups || []);
      setTemplates(t.data.templates || []);
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 3000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const connectedSessions = sessions.filter(s => s.status === 'connected');
  const sessionLabel = (sid) => {
    const s = sessions.find(x => x.id === sid);
    return s ? `${s.name}${s.phone ? ` (${s.phone})` : ''}` : sid;
  };

  const resetWizard = () => {
    setStep(1);
    setCampaignName(''); setSessionId(''); setTemplate(''); setMedia(null);
    setDelayMin(30); setDelayMax(80); setBatchSizeMin(18); setBatchSizeMax(22); setPauseMinMinutes(55); setPauseMaxMinutes(75); setAiVary(false); setAiVariationCount(20); setGroupId(''); setNewGroupName(''); setRecipients([]);
    setRecipientsRaw(''); setSampleNumber(DEFAULT_SAMPLE_NUMBER); setSampleSent(false);
    setScheduleEnabled(false); setScheduleDate(''); setScheduleTime('');
  };

  const canNext = () => {
    if (step === 1) return !!sessionId;
    if (step === 2) return !!campaignName.trim() && !!template.trim();
    if (step === 3) return !!groupId || newGroupName.trim().length > 0;
    if (step === 4) return recipients.length > 0;
    if (step === 5) return true; // sample optional
    return false;
  };

  const goNext = async () => {
    // On step 3 -> if creating new group, save it
    if (step === 3 && !groupId && newGroupName.trim()) {
      try {
        const r = await axios.post(`${API}/wabot/contact-groups`, { name: newGroupName.trim(), contacts: [] });
        setGroupId(r.data.id);
        await fetchAll();
      } catch { toast.error('שגיאה ביצירת קבוצה'); return; }
    }
    // On step 4 -> save contacts to group
    if (step === 4 && groupId && recipients.length > 0) {
      try {
        await axios.put(`${API}/wabot/contact-groups/${groupId}`, { contacts: recipients });
      } catch {}
    }
    setStep(s => Math.min(6, s + 1));
  };

  const goBack = () => setStep(s => Math.max(1, s - 1));

  const sendSample = async () => {
    if (!sampleNumber.trim()) { toast.error('הזן מספר טלפון'); return; }
    try {
      await axios.post(`${API}/wabot/send`, {
        session_id: sessionId,
        to: sampleNumber,
        text: template,
        media_url: media?.url,
        media_type: media?.type,
      });
      setSampleSent(true);
      toast.success('דוגמא נשלחה בהצלחה!');
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה בשליחה'); }
  };

  const launchCampaign = async () => {
    setLaunching(true);
    try {
      // Build optional scheduled_for ISO string in Asia/Jerusalem TZ
      let scheduledForIso = null;
      if (scheduleEnabled) {
        if (!scheduleDate || !scheduleTime) {
          toast.error('בחר תאריך ושעה לתזמון'); setLaunching(false); return;
        }
        // Build a local Date and convert to ISO with explicit +03:00/+02:00 offset
        // by using the browser's locale. Simpler: send naive datetime - server
        // treats it as Asia/Jerusalem.
        scheduledForIso = `${scheduleDate}T${scheduleTime}:00`;
        const target = new Date(scheduledForIso);
        if (isNaN(target.getTime()) || target <= new Date()) {
          toast.error('תאריך/שעה חייבים להיות בעתיד'); setLaunching(false); return;
        }
      }
      await axios.post(`${API}/wabot/campaigns`, {
        name: campaignName,
        session_id: sessionId,
        template,
        recipients,
        delay_min: delayMin,
        delay_max: delayMax,
        batch_size_min: batchSizeMin,
        batch_size_max: batchSizeMax,
        pause_min_minutes: pauseMinMinutes,
        pause_max_minutes: pauseMaxMinutes,
        ai_vary_messages: aiVary,
        ai_variation_count: aiVariationCount,
        media_url: media?.url,
        media_type: media?.type,
        scheduled_for: scheduledForIso,
      });
      toast.success(scheduledForIso ? `הקמפיין מתוזמן ל-${scheduleDate} ${scheduleTime} ⏰` : 'הקמפיין יצא לדרך! 🚀');
      resetWizard();
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
    setLaunching(false);
  };

  const pause = async (c) => { await axios.post(`${API}/wabot/campaigns/${c.id}/pause`); fetchAll(); };
  const openResume = (c) => { setResumeModal(c); setResumeSession(c.session_id); };
  const confirmResume = async () => {
    try {
      await axios.post(`${API}/wabot/campaigns/${resumeModal.id}/resume`, { session_id: resumeSession });
      toast.success('קמפיין ממשיך!');
      setResumeModal(null);
      fetchAll();
    } catch { toast.error('שגיאה'); }
  };
  const remove = async (c) => {
    if (!window.confirm(`למחוק את "${c.name}"?`)) return;
    await axios.delete(`${API}/wabot/campaigns/${c.id}`);
    fetchAll();
  };

  if (loading) return <div className="p-8 text-center text-stone-400">טוען...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto" dir="rtl" data-testid="wabot-marketing">
      <div className="mb-6">
        <h2 className="text-2xl font-black text-[#2C3E2D]">דיוור הודעות וואצאפ</h2>
        <p className="text-sm text-stone-500 mt-1">שלב אחר שלב — ממכשיר ועד סטטיסטיקת לחיצות על התבנית.</p>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-2xl p-6 border border-stone-200 mb-6 shadow-sm">
        <div className="flex items-center justify-between" data-testid="wabot-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <button
                onClick={() => s.id < step && setStep(s.id)}
                className="flex flex-col items-center gap-2 group flex-shrink-0"
                disabled={s.id > step}
                data-testid={`wabot-step-${s.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all ${
                  s.id < step ? 'bg-green-500 text-white' :
                  s.id === step ? 'bg-blue-500 text-white ring-4 ring-blue-100' :
                  'bg-stone-100 text-stone-400'
                }`}>
                  {s.id < step ? <Check className="w-5 h-5" /> : s.id}
                </div>
                <span className={`text-xs font-bold whitespace-nowrap ${s.id === step ? 'text-blue-600' : s.id < step ? 'text-green-600' : 'text-stone-400'}`}>
                  {s.label}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 -mt-6 ${s.id < step ? 'bg-green-300' : 'bg-stone-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm mb-6" data-testid={`wabot-step-content-${step}`}>
        {step === 1 && (
          <Step1 sessions={connectedSessions} sessionId={sessionId} setSessionId={setSessionId} />
        )}
        {step === 2 && (
          <Step2
            campaignName={campaignName} setCampaignName={setCampaignName}
            template={template} setTemplate={setTemplate}
            media={media} setMedia={setMedia}
            delayMin={delayMin} setDelayMin={setDelayMin}
            delayMax={delayMax} setDelayMax={setDelayMax}
            batchSizeMin={batchSizeMin} setBatchSizeMin={setBatchSizeMin}
            batchSizeMax={batchSizeMax} setBatchSizeMax={setBatchSizeMax}
            pauseMinMinutes={pauseMinMinutes} setPauseMinMinutes={setPauseMinMinutes}
            pauseMaxMinutes={pauseMaxMinutes} setPauseMaxMinutes={setPauseMaxMinutes}
            aiVary={aiVary} setAiVary={setAiVary}
            aiVariationCount={aiVariationCount} setAiVariationCount={setAiVariationCount}
            templates={templates} refreshTemplates={fetchAll}
          />
        )}
        {step === 3 && (
          <Step3
            groups={groups} groupId={groupId} setGroupId={setGroupId}
            newGroupName={newGroupName} setNewGroupName={setNewGroupName}
            refreshGroups={fetchAll}
          />
        )}
        {step === 4 && (
          <Step4
            recipientsRaw={recipientsRaw} setRecipientsRaw={setRecipientsRaw}
            recipients={recipients} setRecipients={setRecipients}
            groupId={groupId} groups={groups}
          />
        )}
        {step === 5 && (
          <Step5
            sampleNumber={sampleNumber} setSampleNumber={setSampleNumber}
            template={template} media={media}
            onSend={sendSample} sent={sampleSent}
          />
        )}
        {step === 6 && (
          <Step6
            campaignName={campaignName} sessionLabel={sessionLabel(sessionId)}
            template={template} media={media} recipients={recipients}
            delayMin={delayMin} delayMax={delayMax}
            scheduleEnabled={scheduleEnabled} setScheduleEnabled={setScheduleEnabled}
            scheduleDate={scheduleDate} setScheduleDate={setScheduleDate}
            scheduleTime={scheduleTime} setScheduleTime={setScheduleTime}
            onLaunch={launchCampaign} launching={launching}
          />
        )}
      </div>

      {/* Wizard Controls */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={goBack}
          disabled={step === 1}
          className="flex items-center gap-1 px-5 py-2.5 rounded-lg border-2 border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-30 font-bold text-sm"
          data-testid="wabot-step-back"
        >
          <ChevronRight className="w-4 h-4" /> חזור
        </button>
        {step < 6 && (
          <button
            onClick={goNext}
            disabled={!canNext()}
            className="flex items-center gap-1 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="wabot-step-next"
          >
            המשך <ChevronLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Active Campaigns */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm" data-testid="wabot-campaigns-list">
        <h3 className="font-black text-lg text-[#2C3E2D] mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          קמפיינים פעילים <span className="bg-stone-100 text-stone-500 text-xs px-2 py-0.5 rounded-full">{campaigns.length}</span>
        </h3>
        {campaigns.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-6">עדיין לא יצא קמפיין. השלם את האשף למעלה והשלח את הראשון!</p>
        ) : (
          <div className="space-y-2">
            {campaigns.map(c => {
              const pct = c.total ? Math.round((c.sent / c.total) * 100) : 0;
              const onBreak = c.on_break;
              const statusColor = onBreak ? 'bg-purple-500' : { running: 'bg-green-500', paused: 'bg-amber-500', completed: 'bg-blue-500', scheduled: 'bg-indigo-500' }[c.status] || 'bg-stone-400';
              const scheduledLabel = c.status === 'scheduled' && c.scheduled_for
                ? new Date(c.scheduled_for).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })
                : null;
              return (
                <div key={c.id} className="flex items-center gap-4 p-3 hover:bg-stone-50 rounded-xl border border-stone-100 cursor-pointer transition-colors" data-testid={`wabot-campaign-${c.id}`} onClick={() => setDetailCampaign(c)}>
                  <div className={`w-10 h-10 rounded-xl ${statusColor} flex items-center justify-center text-white`}>
                    <Send className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#2C3E2D] truncate">[{c.name}]</p>
                    <p className="text-xs text-stone-400">
                      {c.sent}/{c.total} נשלחו · {new Date(c.created_at).toLocaleDateString('he-IL')}
                      {c.delay_min && c.delay_max ? ` · ${c.delay_min}-${c.delay_max}ש'` : ''}
                      {onBreak && <span className="text-purple-600 font-bold mr-2">⏸️ בהפסקה (מנוחה אוטומטית)</span>}
                      {!onBreak && c.status === 'running' && c.next_send_in ? <span className="text-stone-500 mr-2">· הבא בעוד ~{c.next_send_in}ש'</span> : ''}
                      {scheduledLabel && <span className="text-indigo-600 font-bold mr-2">⏰ מתוזמן ל-{scheduledLabel}</span>}
                    </p>
                  </div>
                  <div className="w-32 hidden md:block">
                    <div className="w-full bg-stone-100 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${statusColor}`} style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[10px] text-stone-400 mt-1 text-center">{pct}%</p>
                  </div>
                  <span className={`text-[10px] font-bold text-white px-2 py-1 rounded-full ${statusColor}`}>
                    {onBreak ? 'הפסקה' : c.status === 'running' ? 'פועל' : c.status === 'paused' ? 'מושהה' : c.status === 'completed' ? 'הושלם' : c.status === 'scheduled' ? 'מתוזמן' : c.status}
                  </span>
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    {c.status === 'running' && (
                      <button onClick={() => pause(c)} title="השהה" className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg" data-testid={`wabot-camp-pause-${c.id}`}><Pause className="w-4 h-4" /></button>
                    )}
                    {(c.status === 'paused' || (c.status === 'completed' && c.failed > 0)) && (
                      <button onClick={() => openResume(c)} title="המשך" className="p-2 text-green-600 hover:bg-green-50 rounded-lg" data-testid={`wabot-camp-resume-${c.id}`}><Play className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => remove(c)} title="מחק" className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`wabot-camp-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Resume Modal */}
      {resumeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setResumeModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} dir="rtl" data-testid="wabot-resume-modal">
            <h3 className="font-black text-xl text-[#2C3E2D] mb-2">המשך קמפיין</h3>
            <p className="text-sm text-stone-500 mb-4">בחר מכשיר להמשך הקמפיין "{resumeModal.name}"</p>
            <select value={resumeSession} onChange={e => setResumeSession(e.target.value)} className="w-full border rounded-lg px-3 py-2.5 text-sm mb-4" data-testid="wabot-resume-session">
              {connectedSessions.map(s => (
                <option key={s.id} value={s.id}>{s.name}{s.phone ? ` (${s.phone})` : ''}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button onClick={confirmResume} className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-lg py-2.5 font-bold" data-testid="wabot-resume-confirm">המשך עכשיו</button>
              <button onClick={() => setResumeModal(null)} className="flex-1 bg-stone-100 hover:bg-stone-200 rounded-lg py-2.5 font-bold">ביטול</button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Details Panel */}
      {detailCampaign && (
        <CampaignDetailsModal
          campaign={detailCampaign}
          onClose={() => setDetailCampaign(null)}
        />
      )}
    </div>
  );
};

// ================== STEPS ==================
const Step1 = ({ sessions, sessionId, setSessionId }) => (
  <div data-testid="step-1-content">
    <h3 className="font-black text-lg text-[#2C3E2D] mb-1">1. בחירת מכשיר</h3>
    <p className="text-sm text-stone-500 mb-4">בחר באיזה מכשיר וואצאפ ישלח את הדיוור. רק מכשירים מחוברים זמינים.</p>
    {sessions.length === 0 ? (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
        <Smartphone className="w-12 h-12 mx-auto text-amber-500 mb-2" />
        <p className="font-bold text-amber-800">אין מכשירים מחוברים</p>
        <p className="text-sm text-amber-700 mt-1">עבור לטאב "המכשירים שלי" והוסף מכשיר תחילה</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sessions.map(s => (
          <button
            key={s.id}
            onClick={() => setSessionId(s.id)}
            className={`text-right p-4 rounded-2xl border-2 transition-all ${sessionId === s.id ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-stone-200 hover:border-stone-300 bg-white'}`}
            data-testid={`step-1-device-${s.id}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${sessionId === s.id ? 'bg-blue-500' : 'bg-green-100'}`}>
                <Smartphone className={`w-6 h-6 ${sessionId === s.id ? 'text-white' : 'text-green-600'}`} />
              </div>
              <div className="flex-1">
                <p className="font-black text-[#2C3E2D]">{s.name}</p>
                {s.phone && <p className="text-xs text-stone-500" dir="ltr">+{s.phone}</p>}
                <span className="inline-block mt-1 text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">מחובר</span>
              </div>
              {sessionId === s.id && <Check className="w-5 h-5 text-blue-500" />}
            </div>
          </button>
        ))}
      </div>
    )}
  </div>
);

const Step2 = ({ campaignName, setCampaignName, template, setTemplate, media, setMedia, delayMin, setDelayMin, delayMax, setDelayMax, batchSizeMin, setBatchSizeMin, batchSizeMax, setBatchSizeMax, pauseMinMinutes, setPauseMinMinutes, pauseMaxMinutes, setPauseMaxMinutes, aiVary, setAiVary, aiVariationCount, setAiVariationCount, templates, refreshTemplates }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [editingTplId, setEditingTplId] = useState(null);
  const [editName, setEditName] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('מקסימום 25MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/wabot/upload-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMedia({ url: r.data.url, type: r.data.type });
      toast.success('מדיה הועלתה');
    } catch { toast.error('שגיאה'); }
    setUploading(false);
  };

  const saveAsTemplate = async () => {
    const name = (campaignName || '').trim() || prompt('שם התבנית?');
    if (!name) return;
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/wabot/templates`, {
        name,
        template,
        media_url: media?.url,
        media_type: media?.type,
      });
      toast.success('תבנית נשמרה!');
      refreshTemplates();
    } catch { toast.error('שגיאה'); }
    setSavingTemplate(false);
  };

  const loadTemplate = (t) => {
    setCampaignName(t.name);
    setTemplate(t.template || '');
    if (t.media_url) setMedia({ url: t.media_url, type: t.media_type });
    else setMedia(null);
    toast.success(`תבנית "${t.name}" נטענה`);
  };

  const renameTemplate = async (t) => {
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/wabot/templates/${t.id}`, { name: editName });
      setEditingTplId(null);
      refreshTemplates();
      toast.success('שם עודכן');
    } catch { toast.error('שגיאה'); }
    setSavingTemplate(false);
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`למחוק את התבנית "${t.name}"?`)) return;
    await axios.delete(`${API}/wabot/templates/${t.id}`);
    refreshTemplates();
    toast.success('תבנית נמחקה');
  };

  return (
    <div data-testid="step-2-content">
      <h3 className="font-black text-lg text-[#2C3E2D] mb-1">2. הקמת תבנית שיווקית</h3>
      <p className="text-sm text-stone-500 mb-4">צור את תוכן ההודעה. ניתן לשמור כתבנית לשימוש חוזר.</p>

      <div className="mb-4">
        <label className="block text-sm font-bold text-stone-700 mb-1">שם הקמפיין / התבנית</label>
        <input value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="קמפיין מאי 2026" className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="step-2-name" />
      </div>

      {/* Random delay range */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4">
        <label className="block text-sm font-bold text-amber-900 mb-1 flex items-center gap-2">
          ⏱️ השהיה רנדומלית בין הודעות (שניות)
          <span className="text-[10px] bg-amber-200 px-2 py-0.5 rounded-full">הודעה אחת בכל פעם</span>
        </label>
        <p className="text-[11px] text-amber-700 mb-2 leading-tight">
          הודעה <b>אחת בלבד</b> נשלחת בכל פעם. אחר-כך ממתין רנדומלית בטווח שתבחר ושולח עוד הודעה אחת. לעולם לא נשלחות כמה ביחד.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-[10px] text-amber-700 mb-1">מ-</p>
            <input type="number" min="2" max="600" value={delayMin} onChange={e => setDelayMin(Math.max(2, parseInt(e.target.value) || 30))} className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white text-center font-bold" data-testid="step-2-delay-min" />
          </div>
          <span className="text-amber-600 font-bold mt-4">←</span>
          <div className="flex-1">
            <p className="text-[10px] text-amber-700 mb-1">עד</p>
            <input type="number" min={delayMin} max="600" value={delayMax} onChange={e => setDelayMax(Math.max(delayMin, parseInt(e.target.value) || 80))} className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white text-center font-bold" data-testid="step-2-delay-max" />
          </div>
        </div>
        <p className="text-xs text-amber-700 mt-2">
          📌 דוגמה: 30-80 = ההודעה הבאה תישלח אחרי בין 30 ל-80 שניות (רנדומלי בכל פעם).
        </p>
      </div>

      {/* Batch size + forced break - both ranges for full unpredictability */}
      <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 mb-4">
        <label className="block text-sm font-bold text-orange-900 mb-1 flex items-center gap-2">
          ☕ הפסקות אוטומטיות (טווח רנדומלי)
          <span className="text-[10px] bg-orange-200 px-2 py-0.5 rounded-full">חיוני נגד בלוק</span>
        </label>
        <p className="text-[11px] text-orange-700 mb-3 leading-tight">
          גם כמות ההודעות וגם משך ההפסקה רנדומליים בכל מחזור — וואטסאפ לא רואה תבנית קבועה.
        </p>

        {/* Messages range */}
        <div className="bg-white border border-orange-200 rounded-lg p-3 mb-2">
          <p className="text-[11px] font-bold text-orange-800 mb-2">אחרי כמה הודעות לעצור?</p>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[10px] text-orange-700 mb-1">מ-</p>
              <input type="number" min="1" max="500" value={batchSizeMin} onChange={e => setBatchSizeMin(Math.max(1, parseInt(e.target.value) || 18))} className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white text-center font-bold" data-testid="step-2-batch-min" />
            </div>
            <span className="text-orange-600 font-bold mt-4">←</span>
            <div className="flex-1">
              <p className="text-[10px] text-orange-700 mb-1">עד</p>
              <input type="number" min={batchSizeMin} max="500" value={batchSizeMax} onChange={e => setBatchSizeMax(Math.max(batchSizeMin, parseInt(e.target.value) || 22))} className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white text-center font-bold" data-testid="step-2-batch-max" />
            </div>
          </div>
        </div>

        {/* Pause range */}
        <div className="bg-white border border-orange-200 rounded-lg p-3">
          <p className="text-[11px] font-bold text-orange-800 mb-2">להפסיק לכמה דקות?</p>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[10px] text-orange-700 mb-1">מ-</p>
              <input type="number" min="1" max="600" value={pauseMinMinutes} onChange={e => setPauseMinMinutes(Math.max(1, parseInt(e.target.value) || 55))} className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white text-center font-bold" data-testid="step-2-pause-min" />
            </div>
            <span className="text-orange-600 font-bold mt-4">←</span>
            <div className="flex-1">
              <p className="text-[10px] text-orange-700 mb-1">עד</p>
              <input type="number" min={pauseMinMinutes} max="600" value={pauseMaxMinutes} onChange={e => setPauseMaxMinutes(Math.max(pauseMinMinutes, parseInt(e.target.value) || 75))} className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white text-center font-bold" data-testid="step-2-pause-max" />
            </div>
          </div>
        </div>

        <p className="text-xs text-orange-700 mt-2">
          📌 דוגמה: 18-22 הודעות → הפסקה של 55-75 דקות → 18-22 שוב.
        </p>
      </div>

      {/* AI message variation - pre-generates variations once per campaign */}
      <div className={`rounded-xl p-4 mb-4 border-2 ${aiVary ? 'bg-purple-50 border-purple-300' : 'bg-stone-50 border-stone-200'}`}>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={aiVary}
            onChange={e => setAiVary(e.target.checked)}
            className="w-5 h-5 accent-purple-600"
            data-testid="step-2-ai-vary"
          />
          <div className="flex-1">
            <p className="font-black text-[#2C3E2D] text-sm">🤖 AI ינסח כל הודעה מחדש (אנטי-בוט)</p>
            <p className="text-xs text-stone-500">
              ה-AI מכין מבעוד מועד מספר ניסוחים שונים של אותה הודעה. כל נמען מקבל ניסוח רנדומלי מתוך הסט.
            </p>
          </div>
        </label>
        {aiVary && (
          <div className="mt-3 bg-white border border-purple-200 rounded-lg p-3">
            <p className="text-xs font-bold text-purple-800 mb-2">כמה ניסוחים שונים להכין? (פעם אחת בלבד בתחילת הקמפיין)</p>
            <input
              type="number"
              min="3"
              max="50"
              value={aiVariationCount}
              onChange={e => setAiVariationCount(Math.max(3, Math.min(50, parseInt(e.target.value) || 20)))}
              className="w-full border border-purple-300 rounded-lg px-3 py-2 text-sm bg-white text-center font-bold"
              data-testid="step-2-ai-count"
            />
            <p className="text-[10px] text-purple-600 mt-1">
              💡 20 ניסוחים מספיקים בדרך כלל — כל לקוח יקבל ניסוח רנדומלי מהסט. החיוב ב-AI הוא חד-פעמי בתחילת הקמפיין.
            </p>
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-sm font-bold text-stone-700 mb-1">תוכן ההודעה</label>
        <textarea value={template} onChange={e => setTemplate(e.target.value)} rows={6} placeholder="שלום! יש לנו מבצע מיוחד עבורך..." className="w-full border rounded-lg px-3 py-3 text-sm" data-testid="step-2-template" />
      </div>

      {/* Media Upload */}
      {media ? (
        <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-center gap-3 mb-4" data-testid="step-2-media-preview">
          {media.type === 'video' ? (
            <video src={`${BACKEND_ORIGIN}${media.url}`} className="w-24 h-24 object-cover rounded-lg" />
          ) : (
            <img src={`${BACKEND_ORIGIN}${media.url}`} alt="" className="w-24 h-24 object-cover rounded-lg" />
          )}
          <div className="flex-1">
            <p className="font-bold text-blue-900 flex items-center gap-1">
              {media.type === 'video' ? <Film className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
              {media.type === 'video' ? 'סרטון' : 'תמונה'} מצורף
            </p>
            <p className="text-xs text-blue-700">יישלח עם כל הודעה בקמפיין</p>
          </div>
          <button onClick={() => setMedia(null)} className="text-red-500 p-2 hover:bg-red-50 rounded-lg" data-testid="step-2-media-remove"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <>
          <input ref={inputRef} type="file" accept="image/*,video/*" onChange={handleFile} className="hidden" data-testid="step-2-media-input" />
          <button onClick={() => inputRef.current?.click()} disabled={uploading} className="w-full bg-blue-50 border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-100 rounded-xl py-4 font-bold flex items-center justify-center gap-2 mb-4" data-testid="step-2-media-attach">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            צרף תמונה / סרטון לקמפיין (אופציונלי)
          </button>
        </>
      )}

      {/* Save as Template Button */}
      <button
        onClick={saveAsTemplate}
        disabled={savingTemplate || !template.trim()}
        className="w-full bg-purple-50 border-2 border-purple-300 text-purple-700 hover:bg-purple-100 rounded-xl py-3 font-bold flex items-center justify-center gap-2 mb-4 disabled:opacity-50"
        data-testid="step-2-save-template"
      >
        {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        💾 שמור כתבנית לשימוש חוזר
      </button>

      {/* Saved Templates */}
      {templates.length > 0 && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mt-4" data-testid="step-2-templates-list">
          <h4 className="font-black text-sm text-stone-700 mb-3 flex items-center gap-2">
            📚 תבניות שמורות ({templates.length})
          </h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {templates.map(t => (
              <div
                key={t.id}
                className="bg-white border-2 border-stone-200 hover:border-blue-400 rounded-lg p-3 flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm"
                data-testid={`step-2-template-${t.id}`}
                onClick={() => editingTplId !== t.id && loadTemplate(t)}
              >
                {t.media_url && (
                  <div className="flex-shrink-0">
                    {t.media_type === 'video' ? <Film className="w-8 h-8 text-purple-400" /> : <img src={`${BACKEND_ORIGIN}${t.media_url}`} alt="" className="w-10 h-10 object-cover rounded" />}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {editingTplId === t.id ? (
                    <input value={editName} onChange={e => setEditName(e.target.value)} onClick={e => e.stopPropagation()} onKeyDown={e => e.key === 'Enter' && renameTemplate(t)} autoFocus className="w-full border rounded px-2 py-1 text-sm" data-testid={`step-2-template-edit-input-${t.id}`} />
                  ) : (
                    <p className="font-bold text-sm text-[#2C3E2D] truncate">{t.name}</p>
                  )}
                  <p className="text-xs text-stone-400 truncate">{(t.template || '').slice(0, 60)}{(t.template || '').length > 60 ? '...' : ''}</p>
                  <p className="text-[10px] text-stone-300">{new Date(t.created_at).toLocaleDateString('he-IL')}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  {editingTplId === t.id ? (
                    <>
                      <button onClick={() => renameTemplate(t)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" data-testid={`step-2-template-save-${t.id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingTplId(null)} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded"><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => loadTemplate(t)} className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded font-bold text-xs flex items-center gap-1" title="בחר ושימוש בתבנית" data-testid={`step-2-template-load-${t.id}`}><Check className="w-3.5 h-3.5" /> בחר</button>
                      <button onClick={() => { setEditingTplId(t.id); setEditName(t.name); }} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="ערוך שם" data-testid={`step-2-template-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteTemplate(t)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="מחק" data-testid={`step-2-template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Step3 = ({ groups, groupId, setGroupId, newGroupName, setNewGroupName, refreshGroups }) => {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [includeDate, setIncludeDate] = useState(true);

  const startEdit = (g) => {
    setEditingId(g.id);
    setEditValue(g.name);
  };

  const saveEdit = async (g) => {
    if (!editValue.trim()) { setEditingId(null); return; }
    try {
      await axios.put(`${API}/wabot/contact-groups/${g.id}`, { name: editValue.trim() });
      toast.success('שם עודכן');
      setEditingId(null);
      refreshGroups();
    } catch { toast.error('שגיאה'); }
  };

  const removeGroup = async (g) => {
    if (!window.confirm(`למחוק את הקבוצה "${g.name}" וכל ${g.count} אנשי הקשר?`)) return;
    try {
      await axios.delete(`${API}/wabot/contact-groups/${g.id}`);
      toast.success('קבוצה נמחקה');
      if (groupId === g.id) setGroupId('');
      refreshGroups();
    } catch { toast.error('שגיאה'); }
  };

  // Auto-append date to new group name when toggle is on
  const dateStr = new Date().toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const finalNewName = includeDate && newGroupName.trim() && !newGroupName.includes(dateStr)
    ? `${newGroupName} - ${dateStr}` : newGroupName;

  return (
    <div data-testid="step-3-content">
      <h3 className="font-black text-lg text-[#2C3E2D] mb-1">3. קבוצת איש קשר</h3>
      <p className="text-sm text-stone-500 mb-4">בחר קבוצה קיימת או צור קבוצה חדשה. ניתן לערוך ולמחוק כל קבוצה.</p>

      {groups.length > 0 && (
        <div className="space-y-2 mb-4">
          <label className="block text-xs font-bold text-stone-600">קבוצות קיימות:</label>
          {groups.map(g => (
            <div key={g.id} className={`p-3 rounded-xl border-2 flex items-center gap-3 transition-all ${groupId === g.id ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-stone-300 bg-white'}`} data-testid={`step-3-group-${g.id}`}>
              <button
                onClick={() => { setGroupId(g.id); setNewGroupName(''); }}
                className="flex items-center gap-3 flex-1 min-w-0 text-right"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${groupId === g.id ? 'bg-blue-500' : 'bg-stone-100'}`}>
                  <Users className={`w-5 h-5 ${groupId === g.id ? 'text-white' : 'text-stone-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === g.id ? (
                    <input value={editValue} onChange={e => setEditValue(e.target.value)} onClick={e => e.stopPropagation()} onKeyDown={e => { if (e.key === 'Enter') saveEdit(g); if (e.key === 'Escape') setEditingId(null); }} autoFocus className="w-full border rounded px-2 py-1 text-sm font-bold" data-testid={`step-3-group-edit-input-${g.id}`} />
                  ) : (
                    <p className="font-bold text-[#2C3E2D] truncate">{g.name}</p>
                  )}
                  <p className="text-xs text-stone-400 flex items-center gap-1">
                    {g.count || 0} אנשי קשר
                    {g.created_at && <span>· {new Date(g.created_at).toLocaleDateString('he-IL')}</span>}
                  </p>
                </div>
                {groupId === g.id && editingId !== g.id && <Check className="w-5 h-5 text-blue-500 flex-shrink-0" />}
              </button>
              <div className="flex gap-1 flex-shrink-0">
                {editingId === g.id ? (
                  <>
                    <button onClick={() => saveEdit(g)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" data-testid={`step-3-group-save-${g.id}`}><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditingId(null)} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(g)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="ערוך שם" data-testid={`step-3-group-edit-${g.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => removeGroup(g)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="מחק" data-testid={`step-3-group-delete-${g.id}`}><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={`p-4 rounded-xl border-2 border-dashed ${!groupId && newGroupName ? 'border-orange-400 bg-orange-50' : 'border-stone-200'}`}>
        <label className="block text-sm font-bold text-stone-700 mb-2">או — קבוצה חדשה:</label>
        <input
          value={newGroupName}
          onChange={e => { setNewGroupName(e.target.value); if (e.target.value) setGroupId(''); }}
          placeholder="למשל: לקוחות חוזרים מאי"
          className="w-full border rounded-lg px-3 py-2.5 text-sm mb-2"
          data-testid="step-3-new-group"
        />
        <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer mb-2">
          <input type="checkbox" checked={includeDate} onChange={e => setIncludeDate(e.target.checked)} className="rounded" data-testid="step-3-include-date" />
          <Calendar className="w-3.5 h-3.5" />
          הוסף תאריך אוטומטי לסוף השם
        </label>
        {newGroupName.trim() && (
          <div className="bg-stone-50 rounded-lg p-2 text-xs">
            <span className="text-stone-500">השם ייווצר כך: </span>
            <span className="font-bold text-[#D97706]" data-testid="step-3-name-preview">{finalNewName}</span>
          </div>
        )}
        {/* Hidden field to expose final name to parent through ref-like pattern via setNewGroupName when navigating next */}
        {newGroupName.trim() && includeDate && finalNewName !== newGroupName && (
          <button
            onClick={() => setNewGroupName(finalNewName)}
            className="mt-2 text-xs text-blue-600 hover:underline"
            data-testid="step-3-apply-date"
          >
            ✓ השתמש בשם המלא עם התאריך
          </button>
        )}
      </div>
    </div>
  );
};

const Step4 = ({ recipientsRaw, setRecipientsRaw, recipients, setRecipients, groupId, groups }) => {
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [duplicatesRemoved, setDuplicatesRemoved] = useState(0);

  useEffect(() => {
    // Pre-fill from selected group on mount
    if (groupId && recipients.length === 0) {
      const g = groups.find(x => x.id === groupId);
      if (g && g.contacts && g.contacts.length > 0) {
        const unique = Array.from(new Set(g.contacts));
        setRecipients(unique);
        setRecipientsRaw(unique.join('\n'));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dedupe: every time recipients changes, remove duplicates
  const dedupeAndSet = (list) => {
    const seen = new Set();
    const unique = [];
    let dups = 0;
    for (const x of list) {
      const v = x.trim();
      if (!v) continue;
      if (seen.has(v)) { dups++; continue; }
      seen.add(v);
      unique.push(v);
    }
    setRecipients(unique);
    setDuplicatesRemoved(dups);
    return unique;
  };

  const parseAndSet = (raw) => {
    setRecipientsRaw(raw);
    const list = raw.split(/[\s,;\n]+/).map(x => x.trim()).filter(Boolean);
    dedupeAndSet(list);
  };

  const handleCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const matches = text.match(/[\d+()\-\s]{8,}/g) || [];
      const list = matches.map(m => m.replace(/\D/g, '')).filter(x => x.length >= 9);
      const merged = [...recipients, ...list];
      const unique = dedupeAndSet(merged);
      setRecipientsRaw(unique.join('\n'));
      const newOnes = unique.length - recipients.length;
      toast.success(`נוספו ${newOnes} מספרים חדשים${list.length - newOnes > 0 ? ` (סוננו ${list.length - newOnes} כפולים)` : ''}`);
    } catch { toast.error('שגיאה בקריאת קובץ'); }
    setImporting(false);
  };

  return (
    <div data-testid="step-4-content">
      <h3 className="font-black text-lg text-[#2C3E2D] mb-1">4. יבוא אנשי קשר</h3>
      <p className="text-sm text-stone-500 mb-4">הדבק רשימת מספרים או יבא קובץ CSV. <b>מספרים כפולים מסוננים אוטומטית.</b></p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-stone-50 rounded-xl p-3 text-center">
          <Hash className="w-6 h-6 mx-auto text-stone-400 mb-1" />
          <p className="text-xs text-stone-500">מספרים ייחודיים</p>
          <p className="text-2xl font-black text-[#2C3E2D]">{recipients.length}</p>
        </div>
        <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx" onChange={handleCsv} className="hidden" data-testid="step-4-csv-input" />
        <button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-blue-50 hover:bg-blue-100 border-2 border-dashed border-blue-300 text-blue-600 rounded-xl p-3 font-bold text-sm flex flex-col items-center justify-center gap-1" data-testid="step-4-import-csv">
          {importing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          יבא CSV / TXT
        </button>
        <button onClick={() => { setRecipients([]); setRecipientsRaw(''); setDuplicatesRemoved(0); }} className="bg-red-50 hover:bg-red-100 text-red-600 rounded-xl p-3 font-bold text-sm flex flex-col items-center justify-center gap-1" data-testid="step-4-clear">
          <Trash2 className="w-5 h-5" /> נקה הכל
        </button>
      </div>

      {duplicatesRemoved > 0 && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800 flex items-center gap-2" data-testid="step-4-dups-info">
          <Check className="w-3.5 h-3.5" />
          סוננו <b>{duplicatesRemoved}</b> מספרים כפולים אוטומטית
        </div>
      )}

      <textarea
        value={recipientsRaw}
        onChange={e => parseAndSet(e.target.value)}
        rows={10}
        placeholder="0501234567&#10;0521234567&#10;0531234567"
        dir="ltr"
        className="w-full border rounded-lg px-3 py-3 text-sm font-mono"
        data-testid="step-4-textarea"
      />
      <p className="text-xs text-stone-400 mt-2">פסיקים, רווחים או שורות חדשות. מספרים ישראליים יומרו אוטומטית ל-972, וכפולים ייווצרו אוטומטית.</p>
    </div>
  );
};

const Step5 = ({ sampleNumber, setSampleNumber, template, media, onSend, sent }) => (
  <div data-testid="step-5-content">
    <h3 className="font-black text-lg text-[#2C3E2D] mb-1">5. שליחת דוגמא</h3>
    <p className="text-sm text-stone-500 mb-4">שלח לעצמך הודעה לבדיקה לפני שליחה לכל הרשימה.</p>

    <div className="bg-stone-50 rounded-xl p-4 mb-4">
      <p className="text-xs font-bold text-stone-600 mb-2">תצוגה מקדימה של ההודעה:</p>
      {media && (
        <div className="bg-white border rounded-lg p-2 mb-2 inline-block">
          {media.type === 'video' ? (
            <video src={`${BACKEND_ORIGIN}${media.url}`} className="w-32 h-32 object-cover rounded" />
          ) : (
            <img src={`${BACKEND_ORIGIN}${media.url}`} alt="" className="w-32 h-32 object-cover rounded" />
          )}
        </div>
      )}
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm whitespace-pre-wrap" dir="auto">
        {template || <span className="text-stone-400">(ההודעה ריקה)</span>}
      </div>
    </div>

    <div className="flex gap-2">
      <input
        value={sampleNumber}
        onChange={e => setSampleNumber(e.target.value)}
        placeholder="0501234567"
        dir="ltr"
        className="flex-1 border rounded-lg px-3 py-2.5 text-sm font-mono"
        data-testid="step-5-number"
      />
      <button onClick={onSend} className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-5 py-2.5 font-bold text-sm flex items-center gap-2" data-testid="step-5-send-sample">
        <Send className="w-4 h-4" /> שלח דוגמא
      </button>
    </div>
    {sent && (
      <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2">
        <Check className="w-4 h-4" /> הדוגמא נשלחה! בדוק את הוואצאפ שלך.
      </div>
    )}
  </div>
);

const Step6 = ({ campaignName, sessionLabel, template, media, recipients, delayMin, delayMax, scheduleEnabled, setScheduleEnabled, scheduleDate, setScheduleDate, scheduleTime, setScheduleTime, onLaunch, launching }) => {
  // Default scheduled date = today, time = next round hour
  React.useEffect(() => {
    if (scheduleEnabled && !scheduleDate) {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      setScheduleDate(`${yyyy}-${mm}-${dd}`);
    }
    if (scheduleEnabled && !scheduleTime) {
      const next = new Date();
      next.setHours(next.getHours() + 1, 0, 0, 0);
      setScheduleTime(`${String(next.getHours()).padStart(2, '0')}:00`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleEnabled]);

  return (
  <div data-testid="step-6-content">
    <h3 className="font-black text-lg text-[#2C3E2D] mb-1">6. שליחה קבוצתית</h3>
    <p className="text-sm text-stone-500 mb-4">סקירה אחרונה לפני שליחה לכל הרשימה.</p>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      <Stat label="שם הקמפיין" value={campaignName} icon={Megaphone} />
      <Stat label="מכשיר שולח" value={sessionLabel} icon={Smartphone} />
      <Stat label="נמענים" value={recipients.length} icon={Users} />
      <Stat label="השהיה" value={`${delayMin}-${delayMax}ש'`} icon={Hash} />
    </div>

    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-4">
      ⏱️ ההודעות יישלחו עם השהיה רנדומלית של <b>{delayMin}-{delayMax} שניות</b>. כל ~2 שעות תהיה הפסקה אוטומטית של 30 דק' להגנה מבלוק.
    </div>

    {/* Optional schedule */}
    <div className={`rounded-xl p-4 mb-4 border-2 ${scheduleEnabled ? 'bg-blue-50 border-blue-300' : 'bg-stone-50 border-stone-200'}`} data-testid="step-6-schedule">
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={scheduleEnabled}
          onChange={e => setScheduleEnabled(e.target.checked)}
          className="w-5 h-5 accent-blue-600"
          data-testid="step-6-schedule-toggle"
        />
        <div className="flex-1">
          <p className="font-black text-[#2C3E2D] text-sm">⏰ תזמן את הקמפיין למועד מסוים</p>
          <p className="text-xs text-stone-500">הקמפיין ימתין ויצא אוטומטית בתאריך והשעה שתבחר (Asia/Jerusalem).</p>
        </div>
      </label>
      {scheduleEnabled && (
        <div className="mt-3 grid grid-cols-2 gap-3" data-testid="step-6-schedule-inputs">
          <div>
            <label className="text-xs font-bold text-blue-700 block mb-1">תאריך</label>
            <input
              type="date"
              value={scheduleDate}
              onChange={e => setScheduleDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="w-full bg-white border border-blue-300 rounded-lg px-3 py-2 text-sm font-bold focus:outline-none focus:border-blue-500"
              data-testid="step-6-schedule-date"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-blue-700 block mb-1">שעה</label>
            <input
              type="time"
              value={scheduleTime}
              onChange={e => setScheduleTime(e.target.value)}
              className="w-full bg-white border border-blue-300 rounded-lg px-3 py-2 text-sm font-bold focus:outline-none focus:border-blue-500"
              data-testid="step-6-schedule-time"
            />
          </div>
        </div>
      )}
    </div>

    <div className="bg-stone-50 rounded-xl p-4 mb-4">
      <p className="text-xs font-bold text-stone-600 mb-2">ההודעה שתישלח:</p>
      {media && (
        <div className="bg-white border rounded-lg p-2 mb-2 inline-block">
          {media.type === 'video' ? (
            <video src={`${BACKEND_ORIGIN}${media.url}`} className="w-32 h-32 object-cover rounded" />
          ) : (
            <img src={`${BACKEND_ORIGIN}${media.url}`} alt="" className="w-32 h-32 object-cover rounded" />
          )}
        </div>
      )}
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm whitespace-pre-wrap" dir="auto">{template}</div>
    </div>

    <button
      onClick={onLaunch}
      disabled={launching}
      className={`w-full text-white rounded-xl py-4 font-black text-lg flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg ${scheduleEnabled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-500 hover:bg-green-600'}`}
      data-testid="step-6-launch"
    >
      {launching ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />}
      {scheduleEnabled
        ? `תזמן ל-${scheduleDate} ${scheduleTime} (${recipients.length} נמענים) ⏰`
        : `שלח קמפיין ל-${recipients.length} נמענים 🚀`}
    </button>
  </div>
  );
};

const Stat = ({ label, value, icon: Icon }) => (
  <div className="bg-stone-50 rounded-xl p-3">
    <Icon className="w-4 h-4 text-stone-400 mb-1" />
    <p className="text-[10px] text-stone-400">{label}</p>
    <p className="font-bold text-[#2C3E2D] text-sm truncate">{value || '-'}</p>
  </div>
);

export default WaBotMarketing;

// =============== Campaign Details Modal ===============
const CampaignDetailsModal = ({ campaign, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState(null); // { jid, phone, name }

  const fetchDetails = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/wabot/campaigns/${campaign.id}/details`);
      setData(r.data);
    } catch (e) { toast.error('שגיאה בטעינת פרטי קמפיין'); }
    setLoading(false);
  }, [campaign.id]);

  useEffect(() => {
    fetchDetails();
    const t = setInterval(fetchDetails, 8000); // live refresh every 8s
    return () => clearInterval(t);
  }, [fetchDetails]);

  const stats = data?.stats || {};
  const recipients = data?.recipients || [];
  const replied = data?.replied_recipients || [];
  const linkStats = data?.link_stats || [];

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-stretch justify-end" onClick={onClose} data-testid="campaign-details-modal">
      <div className="bg-stone-50 w-full max-w-5xl h-full flex" onClick={e => e.stopPropagation()} dir="rtl">
        {/* Right: stats + recipients */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-1">פרטי קמפיין</p>
              <h2 className="text-2xl font-black text-[#2C3E2D]">[{campaign.name}]</h2>
              <p className="text-sm text-stone-500 mt-1">
                {new Date(campaign.created_at).toLocaleString('he-IL')} · {campaign.delay_min}-{campaign.delay_max}ש'
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-stone-200 rounded-lg" data-testid="campaign-details-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {/* Stat cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard icon={Send} color="blue" label="נשלחו" value={`${stats.sent || 0}/${stats.total || 0}`} />
                <StatCard icon={MessageCircle} color="green" label="התחילו שיחה" value={stats.replied || 0} highlight={(stats.replied || 0) > 0} />
                <StatCard icon={MousePointerClick} color="purple" label="לחצו על לינק" value={stats.unique_clickers || 0} sub={stats.total_clicks ? `${stats.total_clicks} סה"כ קליקים` : null} />
                <StatCard icon={X} color="red" label="נכשלו" value={stats.failed || 0} />
              </div>

              {/* Live indicator */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center gap-2 text-xs">
                <Activity className="w-4 h-4 text-blue-600 animate-pulse" />
                <span className="font-bold text-blue-700">חי</span>
                <span className="text-blue-600">הנתונים מתעדכנים אוטומטית כל 8 שניות</span>
              </div>

              {/* Replied list */}
              {replied.length > 0 && (
                <div className="bg-white rounded-2xl border-2 border-green-200 p-4 mb-4">
                  <h3 className="font-black text-[#2C3E2D] mb-3 flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                    התחילו שיחה ({replied.length})
                  </h3>
                  <div className="space-y-2">
                    {replied.map(r => (
                      <button
                        key={r.jid}
                        onClick={() => setActiveChat({ jid: r.jid, phone: r.phone, recipient: r })}
                        className={`w-full text-right flex items-center gap-3 p-3 rounded-lg transition-colors border ${activeChat?.jid === r.jid ? 'bg-green-100 border-green-400' : 'bg-green-50 hover:bg-green-100 border-green-200'}`}
                        data-testid={`campaign-replied-${r.jid}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-black flex-shrink-0">
                          <Phone className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#2C3E2D] truncate" dir="ltr">+{r.phone}</p>
                          <p className="text-xs text-stone-600 truncate" dir="auto">
                            {r.last_reply_text ? `"${r.last_reply_text}"` : 'תגובה התקבלה'}
                          </p>
                        </div>
                        <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                          {r.replied_count || 1} תגובות
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Link stats */}
              {linkStats.length > 0 && (
                <div className="bg-white rounded-2xl border-2 border-purple-200 p-4 mb-4">
                  <h3 className="font-black text-[#2C3E2D] mb-3 flex items-center gap-2">
                    <ExternalLink className="w-5 h-5 text-purple-600" />
                    קליקים על לינקים
                  </h3>
                  <div className="space-y-2">
                    {linkStats.map(ls => (
                      <div key={ls.idx} className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3">
                        <span className="bg-purple-600 text-white text-xs font-black w-7 h-7 rounded-full flex items-center justify-center">{ls.idx + 1}</span>
                        <a href={ls.url} target="_blank" rel="noreferrer" className="flex-1 text-sm text-purple-700 hover:underline truncate" dir="ltr">{ls.url}</a>
                        <div className="text-left text-xs text-stone-700 flex-shrink-0">
                          <p className="font-bold">{ls.unique_clickers} ייחודיים</p>
                          <p className="text-stone-400">{ls.total_clicks} סה"כ</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All recipients */}
              <div className="bg-white rounded-2xl border-2 border-stone-200 p-4">
                <h3 className="font-black text-[#2C3E2D] mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-stone-600" />
                  כל הנמענים ({recipients.length})
                </h3>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {recipients.map(r => {
                    const badge = {
                      replied: { cls: 'bg-green-500 text-white', label: 'הגיב' },
                      sent: { cls: 'bg-blue-100 text-blue-700', label: 'נשלח' },
                      failed: { cls: 'bg-red-100 text-red-700', label: 'נכשל' },
                      pending: { cls: 'bg-stone-100 text-stone-500', label: 'ממתין' },
                    }[r.status_label] || { cls: 'bg-stone-100 text-stone-500', label: '?' };
                    return (
                      <button
                        key={r.jid}
                        onClick={() => setActiveChat({ jid: r.jid, phone: r.phone, recipient: r })}
                        className={`w-full text-right flex items-center gap-3 p-2 rounded-lg transition-colors ${activeChat?.jid === r.jid ? 'bg-blue-50' : 'hover:bg-stone-50'}`}
                        data-testid={`campaign-recipient-${r.jid}`}
                      >
                        <span dir="ltr" className="font-mono text-sm text-stone-700 flex-1 text-right">+{r.phone}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Left: chat panel */}
        {activeChat && (
          <CampaignChatPanel
            campaign={campaign}
            jid={activeChat.jid}
            phone={activeChat.phone}
            onClose={() => setActiveChat(null)}
          />
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, color, label, value, sub, highlight }) => {
  const palette = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  }[color] || 'bg-stone-50 border-stone-200 text-stone-700';
  return (
    <div className={`rounded-2xl border-2 p-4 ${palette} ${highlight ? 'shadow-lg ring-2 ring-green-300' : ''}`}>
      <Icon className="w-5 h-5 mb-2 opacity-70" />
      <p className="text-3xl font-black">{value}</p>
      <p className="text-xs font-bold opacity-80">{label}</p>
      {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
};

const CampaignChatPanel = ({ campaign, jid, phone, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/wabot/campaigns/${campaign.id}/conversation/${encodeURIComponent(jid)}`);
      setMessages(r.data.messages || []);
    } catch (e) {}
    setLoading(false);
  }, [campaign.id, jid]);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 5000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  const sendReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/wabot/send`, {
        session_id: campaign.session_id,
        to: jid,
        text: reply,
      });
      setReply('');
      // Optimistically append outgoing
      setMessages(m => [...m, { direction: 'out', text: reply, sent_at: new Date().toISOString() }]);
      toast.success('נשלח');
    } catch (e) { toast.error('שגיאה בשליחה'); }
    setSending(false);
  };

  return (
    <div className="w-full max-w-md bg-white border-r-2 border-stone-200 flex flex-col h-full" data-testid="campaign-chat-panel">
      <div className="p-4 border-b bg-green-500 text-white flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><Phone className="w-5 h-5" /></div>
        <div className="flex-1">
          <p className="font-black">+{phone}</p>
          <p className="text-xs opacity-80">צ'אט בקמפיין</p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 bg-[#ECE5DD] space-y-2">
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-green-600 mx-auto mt-8" />
        ) : messages.length === 0 ? (
          <p className="text-center text-stone-500 text-sm mt-8">אין הודעות עדיין.<br/>הקמפיין נשלח אך הלקוח עוד לא הגיב.</p>
        ) : messages.map((m, i) => (
          <div key={i} className={`flex ${m.direction === 'out' ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap shadow-sm ${m.direction === 'out' ? 'bg-[#DCF8C6]' : 'bg-white'}`} dir="auto">
              {m.text}
              <p className="text-[9px] text-stone-500 mt-1">{m.received_at || m.sent_at ? new Date(m.received_at || m.sent_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t bg-stone-50 flex gap-2">
        <input
          value={reply}
          onChange={e => setReply(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendReply()}
          placeholder="הקלד תגובה..."
          className="flex-1 border border-stone-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-green-500"
          data-testid="campaign-chat-input"
        />
        <button
          onClick={sendReply}
          disabled={!reply.trim() || sending}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full p-2.5 disabled:opacity-50"
          data-testid="campaign-chat-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};
